/**
 * Sorterings-typer og -logikk for prosjektlistene. Skilt ut fra ProjectTable
 * så både komponenten og ProjectsPage (som persisterer sorteringen over
 * navigasjon) kan dele dem uten å bryte react-refresh (komponent-fil skal kun
 * eksportere komponenter).
 *
 * VIB-2518: unionen dekker BÅDE aktiv-lista (ProjectTable) og avsluttet-lista
 * (CompletedProjectsTable). Tidligere hadde avsluttet-tabellen sin egen lokale
 * kopi av typene og toggle-logikken, som gjorde at URL-sorteringen fra
 * aktiv-lista ble ignorert ved bytte til «Avsluttede».
 */
import type { DBProject } from "@/lib/db-types";
import type { ProjectHours } from "@/hooks/use-project-hours";
import { estimertTimepris, reellTimepris } from "@/lib/project-rates";
import { budsjettavvikPct } from "@/lib/completed-projects";

export type SortKey =
  // Felles
  | "code"
  | "name"
  | "salg"
  | "advisor"
  | "pl"
  // Aktiv-lista
  | "hours"
  | "est_rate"
  | "live_rate"
  | "bf_est"
  | "faktura"
  | "deadline"
  // Avsluttet-lista
  | "bf_actual"
  | "timepris"
  | "solgt"
  | "brukt"
  | "avvik"
  | "dato"
  | "lagring"
  | null;
export type SortDir = "asc" | "desc";

/** Alle gyldige (ikke-null) sort-nøkler — brukes til å validere persistert state. */
export const SORT_KEYS: Exclude<SortKey, null>[] = [
  "code", "name", "salg", "advisor", "pl", "hours",
  "est_rate", "live_rate", "bf_est", "faktura", "deadline",
  "bf_actual", "timepris", "solgt", "brukt", "avvik", "dato", "lagring",
];

/**
 * Tallkolonner der «størst først» er det nyttige første klikket. Tekst- og
 * datokolonner (code, name, salg, advisor, pl, deadline, dato) starter asc.
 */
const DESC_FIRST_KEYS = new Set<Exclude<SortKey, null>>([
  "bf_est", "bf_actual", "timepris", "solgt", "brukt", "avvik", "lagring",
]);

/**
 * Neste sort-tilstand ved klikk på en kolonne-header: samme nøkkel toggler
 * retning, ny nøkkel starter asc (tallkolonnene starter desc). Delt mellom
 * lokal (uncontrolled) og persistert (controlled via ProjectsPage) sortering.
 */
export function nextSortState(
  prevKey: SortKey,
  prevDir: SortDir,
  key: SortKey,
): { key: SortKey; dir: SortDir } {
  if (prevKey === key) return { key, dir: prevDir === "asc" ? "desc" : "asc" };
  return { key, dir: key && DESC_FIRST_KEYS.has(key) ? "desc" : "asc" };
}

/** Minimums-formen av userMap som navne-sorteringen trenger. */
type NameLookup = Map<string, { full_name: string }>;

/**
 * Navn på (alfabetisk første) salgseier — brukes til å sortere Salg-kolonnen
 * i både aktiv- og avsluttet-lista.
 */
export function firstSalesName(ids: string[], userMap: NameLookup): string {
  return (
    ids
      .map((id) => userMap.get(id)?.full_name ?? "")
      .filter(Boolean)
      .sort((x, y) => x.localeCompare(y, "nb"))[0] ?? ""
  );
}

/** Navn på én bruker (Rådgiver/PL), tom streng når id mangler eller er ukjent. */
export function userName(id: string | null | undefined, userMap: NameLookup): string {
  return id ? userMap.get(id)?.full_name ?? "" : "";
}

/** Minimums-formen av et prosjekt som aktiv-sorteringen trenger. */
export type ActiveSortRow = Pick<
  DBProject,
  | "id"
  | "project_code"
  | "name"
  | "total_amount"
  | "bf_estimated"
  | "bf_actual"
  | "estimated_deadline"
  | "production_lead_id"
  | "advisor_id"
>;

export interface ActiveSortContext {
  ownersMap: Map<string, string[]>;
  userMap: NameLookup;
  hoursMap: Map<string, ProjectHours>;
  invoicedMap?: Map<string, number>;
}

/** Umålbar timepris (null) regnes som lavest ved sortering. */
function rateForSort(rate: number | null): number {
  return rate ?? -1;
}

/**
 * VIB-2528: sorteringen for aktiv-lista er en ren funksjon, delt mellom
 * ProjectTable (visning) og ProjectsPage (piltast-navigasjon), så tastaturet
 * alltid følger den viste rekkefølgen. `sortKey === null` gir input uendret.
 */
export function sortActiveProjects<T extends ActiveSortRow>(
  projects: readonly T[],
  sortKey: SortKey,
  sortDir: SortDir,
  ctx: ActiveSortContext,
): T[] {
  if (!sortKey) return [...projects];
  const { ownersMap, userMap, hoursMap, invoicedMap = new Map() } = ctx;
  const faktRatio = (id: string, total: number): number =>
    total > 0 ? (invoicedMap.get(id) ?? 0) / total : 0;

  return [...projects].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "code": {
        const codeA = parseInt(a.project_code ?? "0") || 0;
        const codeB = parseInt(b.project_code ?? "0") || 0;
        cmp = codeA - codeB;
        break;
      }
      case "name":
        cmp = (a.name ?? "").localeCompare(b.name ?? "", "nb");
        break;
      case "salg":
        cmp = firstSalesName(ownersMap.get(a.id) ?? [], userMap).localeCompare(
          firstSalesName(ownersMap.get(b.id) ?? [], userMap),
          "nb",
        );
        break;
      case "advisor":
        cmp = userName(a.advisor_id, userMap).localeCompare(userName(b.advisor_id, userMap), "nb");
        break;
      case "pl":
        cmp = userName(a.production_lead_id, userMap).localeCompare(
          userName(b.production_lead_id, userMap),
          "nb",
        );
        break;
      case "hours":
        cmp = (hoursMap.get(a.id)?.logged ?? 0) - (hoursMap.get(b.id)?.logged ?? 0);
        break;
      case "est_rate":
        cmp =
          rateForSort(estimertTimepris(a.bf_estimated, hoursMap.get(a.id)?.budgeted ?? 0)) -
          rateForSort(estimertTimepris(b.bf_estimated, hoursMap.get(b.id)?.budgeted ?? 0));
        break;
      case "live_rate":
        cmp =
          rateForSort(reellTimepris(a.bf_actual, hoursMap.get(a.id)?.logged ?? 0)) -
          rateForSort(reellTimepris(b.bf_actual, hoursMap.get(b.id)?.logged ?? 0));
        break;
      case "bf_est":
        cmp = (a.bf_estimated ?? -999999) - (b.bf_estimated ?? -999999);
        break;
      case "faktura":
        cmp =
          faktRatio(a.id, Number(a.total_amount ?? 0)) -
          faktRatio(b.id, Number(b.total_amount ?? 0));
        break;
      case "deadline": {
        // Missing deadlines sort to end regardless of direction
        const dA = a.estimated_deadline ?? null;
        const dB = b.estimated_deadline ?? null;
        if (dA === dB) cmp = 0;
        else if (dA === null) return 1;
        else if (dB === null) return -1;
        else cmp = dA.localeCompare(dB);
        break;
      }
    }
    return sortDir === "asc" ? cmp : -cmp;
  });
}

/** Avledet rad for avsluttet-lista (lønnsomhet/leveranse). */
export interface CompletedRow {
  id: string;
  code: string | null;
  name: string;
  salesIds: string[];
  advisorId: string | null;
  plId: string | null;
  bfActual: number;
  timerSolgt: number;
  timerBrukt: number;
  timepris: number | null;
  avvik: number | null;
  dato: string | null;
  /** VIB-2103: lagret datamengde + lokasjon. null naar prosjektet ikke har avtale. */
  storageGb: number | null;
  storageLocation: string | null;
}

export function toCompletedRow(
  p: DBProject,
  ownersMap: Map<string, string[]>,
  hoursMap: Map<string, ProjectHours>,
  /** VIB-2103. Utelatt = kolonnen viser «—», som naar flagget er av. */
  storageMap?: Map<string, { gb: number | null; location: string | null }>,
): CompletedRow {
  const h = hoursMap.get(p.id);
  const timerSolgt = h?.budgeted ?? 0;
  const timerBrukt = h?.logged ?? 0;
  const bfActual = Number(p.bf_actual ?? 0);
  return {
    id: p.id,
    code: p.project_code,
    name: p.name,
    salesIds: ownersMap.get(p.id) ?? [],
    advisorId: p.advisor_id ?? null,
    plId: p.production_lead_id ?? null,
    bfActual,
    timerSolgt,
    timerBrukt,
    timepris: reellTimepris(bfActual, timerBrukt),
    avvik: budsjettavvikPct(timerSolgt, timerBrukt),
    dato: p.completed_at ?? null,
    storageGb: storageMap?.get(p.id)?.gb ?? null,
    storageLocation: storageMap?.get(p.id)?.location ?? null,
  };
}

/** VIB-2528: samme deling som sortActiveProjects, for avsluttet-lista. */
export function sortCompletedRows(
  rows: readonly CompletedRow[],
  sortKey: SortKey,
  sortDir: SortDir,
  userMap: NameLookup,
): CompletedRow[] {
  if (!sortKey) return [...rows];
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "code": cmp = (parseInt(a.code ?? "0") || 0) - (parseInt(b.code ?? "0") || 0); break;
      case "name": cmp = a.name.localeCompare(b.name, "nb"); break;
      case "salg":
        cmp = firstSalesName(a.salesIds, userMap).localeCompare(
          firstSalesName(b.salesIds, userMap),
          "nb",
        );
        break;
      case "advisor":
        cmp = userName(a.advisorId, userMap).localeCompare(userName(b.advisorId, userMap), "nb");
        break;
      case "pl":
        cmp = userName(a.plId, userMap).localeCompare(userName(b.plId, userMap), "nb");
        break;
      case "bf_actual": cmp = a.bfActual - b.bfActual; break;
      case "timepris": cmp = (a.timepris ?? -1) - (b.timepris ?? -1); break;
      case "solgt": cmp = a.timerSolgt - b.timerSolgt; break;
      case "brukt": cmp = a.timerBrukt - b.timerBrukt; break;
      case "avvik": cmp = (a.avvik ?? -Infinity) - (b.avvik ?? -Infinity); break;
      case "lagring": cmp = (a.storageGb ?? -1) - (b.storageGb ?? -1); break;
      case "dato": {
        const da = a.dato ?? "", db = b.dato ?? "";
        cmp = da.localeCompare(db); break;
      }
    }
    return sortDir === "asc" ? cmp : -cmp;
  });
}
